import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useSortedPaginated } from '../components/TableControls';
import { formatDate, formatMonthYear } from '../utils/dateUtils';
import {
  Button,
  Badge,
  StatCard,
  StatGrid,
  DataTable,
  Textarea,
  Breadcrumb,
} from '../components/ui';

const TIPO_ES = {
  purchase: 'Compra',
  sale: 'Venta',
  waste: 'Merma',
  transfer_in: 'Transferencia entrada',
  transfer_out: 'Transferencia salida',
  adjustment: 'Ajuste',
  initial: 'Inicial',
};

// Status → badge tone + Spanish label. `invalid` carries a longer label in the
// header (it tells the user the required next step).
const STATUS = {
  finalized: { tone: 'success', label: 'Finalizado' },
  invalid: { tone: 'danger', label: 'Inválido' },
  draft: { tone: 'warning', label: 'Borrador' },
};

// Columns for the reconciliation detail table. align:'right' marks numeric
// columns; `sortable` keys map to the fields useSortedPaginated sorts on.
const COLUMNS = [
  { key: 'item_name', label: 'Artículo', align: 'left', sortable: true },
  { key: 'item_category', label: 'Categoría', align: 'left', sortable: true },
  { key: 'starting_quantity', label: 'Inicial', align: 'right', sortable: true },
  { key: 'purchases', label: 'Compras', align: 'right', sortable: true },
  { key: 'sales', label: 'Ventas', align: 'right', sortable: true },
  { key: 'transfers_in', label: 'Entrada', align: 'right', sortable: true },
  { key: 'transfers_out', label: 'Salida', align: 'right', sortable: true },
  { key: 'waste', label: 'Merma', align: 'right', sortable: true },
  { key: 'waste_marketing', label: 'Mkt', align: 'right', title: 'Marketing' },
  { key: 'waste_factory', label: 'Fab', align: 'right', title: 'Fábrica' },
  { key: 'waste_gifts', label: 'Reg', align: 'right', title: 'Regalos' },
  { key: 'waste_other', label: 'Otro', align: 'right', title: 'Otro' },
  { key: 'expected_quantity', label: 'Esperado', align: 'right', sortable: true },
  { key: 'counted_quantity', label: 'Contado', align: 'right', sortable: true },
  { key: 'discrepancy', label: 'Dif.', align: 'right', sortable: true },
  { key: 'estado', label: 'Estado', align: 'left' },
  { key: 'acciones', label: 'Acciones', align: 'left' },
];

// Sign-based color for a discrepancy figure: shortage (negative) reads danger,
// surplus (positive) reads success, and a reconciled 0 is muted (text-3).
function discClass(d) {
  if (d < 0) return 'ui-disc--danger';
  if (d > 0) return 'ui-disc--success-text';
  return 'ui-disc--zero';
}

export default function ReconciliationDetail() {
  const { reconId } = useParams();
  const navigate = useNavigate();
  const [recon, setRecon] = useState(null);
  const [msg, setMsg] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState('');

  const items = recon?.items || [];
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(items, 'discrepancy', 'asc');

  const load = useCallback(() => {
    api.get(`/reconciliation/${reconId}`).then(r => setRecon(r.data));
  }, [reconId]);

  useEffect(() => { load(); }, [load]);

  const finalize = async () => {
    if (!window.confirm('¿Finalizar esta conciliación? Esto bloqueará el mes.')) return;
    try {
      await api.post(`/reconciliation/${reconId}/finalize`);
      setMsg('¡Conciliación finalizada!');
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  const investigate = async (item) => {
    setSelectedItem(item);
    setNotes(item.investigation_notes || '');
    const res = await api.get(`/reports/item-history/${recon.store_id}/${item.item_id}?month_year=${recon.month_year}`);
    setHistory(res.data);
  };

  const saveNotes = async () => {
    await api.put(`/reconciliation/${reconId}/items/${selectedItem.item_id}/notes`, { notes });
    setMsg('¡Notas guardadas!');
    load();
    setSelectedItem(null);
  };

  const downloadPdf = () => {
    window.open(`${api.defaults.baseURL}/reports/reconciliation/${reconId}/pdf?token=${localStorage.getItem('token')}`, '_blank');
  };

  const downloadExcel = () => {
    window.open(`${api.defaults.baseURL}/reports/reconciliation/${reconId}/excel?token=${localStorage.getItem('token')}`, '_blank');
  };

  if (!recon) return <p style={{ color: 'var(--text-2)' }}>Cargando...</p>;

  const status = STATUS[recon.status] || STATUS.draft;
  const statusLabel = recon.status === 'invalid' ? 'Inválida — eliminar y regenerar' : status.label;

  return (
    <div>
      {/* Header: breadcrumb + title, status badge + actions */}
      <div className="page-header">
        <div>
          <Breadcrumb items={['Conciliaciones', recon.store_name]} />
          <h1 style={{ marginTop: 5 }}>Conciliación — {formatMonthYear(recon.month_year)}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge tone={status.tone}>{statusLabel}</Badge>
          {recon.status === 'draft' && (
            <Button variant="primary" onClick={finalize}>Finalizar</Button>
          )}
          <Button variant="secondary" onClick={downloadPdf}>⬇ PDF</Button>
          <Button variant="secondary" onClick={downloadExcel}>Excel</Button>
          <Button variant="secondary" onClick={() => navigate('/reconciliations')}>Volver</Button>
        </div>
      </div>

      {msg && <div className={`alert ${msg.includes('Error') ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}

      {/* KPI cards */}
      <StatGrid className="recon-kpis">
        <StatCard label="Total esperado" value={recon.total_expected.toFixed(1)} sub="unidades" />
        <StatCard label="Total contado" value={recon.total_counted.toFixed(1)} sub="unidades" />
        <StatCard
          label="Discrepancia"
          value={recon.total_discrepancy.toFixed(1)}
          tone={recon.total_discrepancy !== 0 ? 'danger' : undefined}
          sub="unidades"
        />
        <StatCard
          label="% Merma (total)"
          value={`${recon.waste_percentage}%`}
          tone={recon.waste_threshold_exceeded ? 'danger' : 'success'}
          sub={recon.waste_threshold_exceeded ? undefined : 'dentro de objetivo'}
        >
          {recon.waste_threshold_exceeded && (
            <div style={{ marginTop: 8 }}><Badge tone="danger">Supera 1.5%</Badge></div>
          )}
        </StatCard>
        <StatCard
          label="% Discrepancia"
          value={`${recon.discrepancy_percentage}%`}
          tone={recon.discrepancy_threshold_exceeded ? 'danger' : 'success'}
          sub="merma desconocida"
        >
          {recon.discrepancy_threshold_exceeded && (
            <div style={{ marginTop: 8 }}><Badge tone="danger">Supera 0.5%</Badge></div>
          )}
        </StatCard>
      </StatGrid>

      <div style={{ height: 24 }} />

      {/* Detail table */}
      <DataTable
        title="Detalles de artículos"
        columns={COLUMNS}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={setSort}
        minWidth={1100}
        page={page}
        perPage={perPage}
        total={total}
        onPage={setPage}
        onPerPage={setPerPage}
      >
        {paginated.map(item => {
          const reconciles = item.discrepancy === 0;
          return (
            <tr key={item.id} className={reconciles ? '' : 'ui-row--discrepancy'}>
              <td>{item.item_name}</td>
              <td style={{ color: 'var(--text-2)' }}>{item.item_category || '—'}</td>
              <td className="ui-num">{item.starting_quantity}</td>
              <td className="ui-num">{item.purchases}</td>
              <td className="ui-num">{item.sales}</td>
              <td className="ui-num">{item.transfers_in}</td>
              <td className="ui-num">{item.transfers_out}</td>
              <td className="ui-num">{item.waste}</td>
              <td className="ui-num">{item.waste_marketing || 0}</td>
              <td className="ui-num">{item.waste_factory || 0}</td>
              <td className="ui-num">{item.waste_gifts || 0}</td>
              <td className="ui-num">{item.waste_other || 0}</td>
              <td className="ui-num">{item.expected_quantity.toFixed(1)}</td>
              <td className="ui-num">{item.counted_quantity}</td>
              <td className={`ui-num ${discClass(item.discrepancy)}`}>{item.discrepancy.toFixed(1)}</td>
              <td>
                <Badge tone={reconciles ? 'success' : 'danger'}>{reconciles ? 'Cuadra' : 'Revisar'}</Badge>
              </td>
              <td>
                <Button variant="secondary" size="sm" onClick={() => investigate(item)}>Investigar</Button>
              </td>
            </tr>
          );
        })}
      </DataTable>

      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <h2>Investigar: {selectedItem.item_name}</h2>
            <div style={{ marginBottom: 12, color: 'var(--text-2)', fontSize: 14 }}>
              <strong style={{ color: 'var(--text)' }}>Esperado:</strong> {selectedItem.expected_quantity.toFixed(1)} &nbsp;|&nbsp;
              <strong style={{ color: 'var(--text)' }}> Contado:</strong> {selectedItem.counted_quantity} &nbsp;|&nbsp;
              <strong style={{ color: 'var(--text)' }}> Diferencia:</strong>{' '}
              <span className={discClass(selectedItem.discrepancy)} style={{ fontWeight: 600 }}>
                {selectedItem.discrepancy.toFixed(1)}
              </span>
            </div>

            <h4 style={{ marginBottom: 8 }}>Historial de Movimientos</h4>
            <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
              <table className="ui-table" style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Tipo</th><th className="ui-num">Cant.</th><th>Motivo</th><th>Por</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td>{formatDate(h.date)}</td>
                      <td>{TIPO_ES[h.type] || h.type}</td>
                      <td className="ui-num">{h.quantity}</td>
                      <td>{h.reason || '—'}</td>
                      <td>{h.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Textarea
              label="Notas de Investigación / Explicación"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="p.ej. Error de conteo, merma no registrada, robo, envío corto del proveedor..."
            />

            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setSelectedItem(null)}>Cerrar</Button>
              <Button variant="primary" onClick={saveNotes}>Guardar Notas</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
